import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { type MeasureInsightTableConnectedProps, useRegisterDrawerRef } from '@grow-therapy-team/seedling-components';

import { useCaptureComponentView } from '../../hooks/useCaptureComponentView';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { mergeRefs } from '../../utils';
import { roomAtom } from '../../video-sdk';
import {
  findMeasureBundleInfoByResponseShortId,
  getMeasureSchemaTypesPresent,
  getScoreCategoryLabelsPresent,
} from '../measures/utils';
import {
  ProviderDrawerState,
  drawerStateAtom,
  measureResponsesEntryContextAtom,
  patientHasCompletedMeasureResponsesFamily,
  patientShortIdWithFetchedMeasuresAtom,
  providerShortIdAtom,
  selectedBundleAtom,
  selectedClientMeasuresData as selectedClientMeasuresDataAtom,
  selectedClientUserShortIdAtom,
  selectedMeasureBundleItemIdAtom,
} from '../state';
import { ClientInformationProgress } from './ClientInformationProgress';
import { useSelectedClientAppointmentIds } from './useSelectedClientAppointmentIds';
import { DrawerRefKey } from './utils/types';

type WrapperProps = Omit<Partial<MeasureInsightTableConnectedProps>, 'clientUserShortId' | 'providerShortId'>;

export function ClientInformationProgressWrapper({
  isProvider = true,
  isShownWithinPaperworkFlow = false,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  ...passthroughProps
}: WrapperProps) {
  const measuresRef = useRegisterDrawerRef<DrawerRefKey, HTMLDivElement>(DrawerRefKey.MEASURES_CARD);
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom);
  const providerShortId = useAtomValue(providerShortIdAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setMeasureResponsesEntryContext = useSetAtom(measureResponsesEntryContextAtom);
  const setSelectedBundle = useSetAtom(selectedBundleAtom);
  const setSelectedMeasureBundleItemId = useSetAtom(selectedMeasureBundleItemIdAtom);

  const { appointmentShortId, patientShortId } = useSelectedClientAppointmentIds();
  const measureBundles = useAtomValue(selectedClientMeasuresDataAtom);
  const hasCompletedMeasureResponses = useAtomValue(patientHasCompletedMeasureResponsesFamily(patientShortId));
  const measuresFetchedForPatient = useAtomValue(patientShortIdWithFetchedMeasuresAtom);
  const isMeasuresDataReady = !!patientShortId && measuresFetchedForPatient === patientShortId;
  const inSession = !!useAtomValue(roomAtom);
  const { userClicked } = useTrackEvent();
  const viewTrackingProperties = useMemo(
    () => ({
      measureProgressView: 'table' as const,
      inSession,
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
      measureSchemaTypesPresent: getMeasureSchemaTypesPresent(measureBundles),
      scoreCategoryLabelsPresent: getScoreCategoryLabelsPresent(measureBundles),
      ...(appointmentShortId ? { appointmentShortId } : {}),
    }),
    [appointmentShortId, inSession, measureBundles, patientShortId, providerShortId],
  );
  const { ref: viewTrackingRef } = useCaptureComponentView({
    type: EventTypeEnum.MEASURE_PROGRESS_VIEWED,
    properties: viewTrackingProperties,
    triggerOnce: true,
    isReady: isMeasuresDataReady,
  });

  const mergedRefs = useMemo(() => mergeRefs(measuresRef, viewTrackingRef), [measuresRef, viewTrackingRef]);

  const handleMeasureResponseClick = useCallback(
    (measureResponseShortId: string) => {
      const { measureBundle: selectedMeasureBundle, measureBundleItem: selectedMeasureBundleItem } = measureBundles
        ? findMeasureBundleInfoByResponseShortId(measureBundles, measureResponseShortId)
        : {};
      if (!measureResponseShortId || !selectedMeasureBundle || !selectedMeasureBundleItem) return;

      userClicked(EventTypeEnum.MEASURE_PROGRESS_CLICKED, {
        measureProgressView: 'table',
        inSession,
        patientShortId: patientShortId ?? '',
        providerShortId: providerShortId ?? '',
        measureResponseShortId,
        measureSchemaType: selectedMeasureBundleItem?.measureSchema?.type ?? null,
        scoreCategoryLabel: selectedMeasureBundleItem?.measureResponse?.scoreCategoryLabel ?? null,
        ...(appointmentShortId ? { appointmentShortId } : {}),
      });

      setSelectedBundle(selectedMeasureBundle);
      setSelectedMeasureBundleItemId(selectedMeasureBundleItem.measureBundleItemId?.toString() ?? '');
      if (patientShortId) {
        setMeasureResponsesEntryContext((currentEntryContext) =>
          currentEntryContext?.entryMethod === 'pre_session_prep_button' &&
          currentEntryContext.patientShortId === patientShortId
            ? currentEntryContext
            : { entryMethod: 'progress_table_row', patientShortId },
        );
      }
      setDrawerState(ProviderDrawerState.MEASURE_RESPONSES);
    },
    [
      appointmentShortId,
      inSession,
      measureBundles,
      patientShortId,
      providerShortId,
      setDrawerState,
      setMeasureResponsesEntryContext,
      setSelectedBundle,
      setSelectedMeasureBundleItemId,
      userClicked,
    ],
  );

  if (!clientUserShortId || !providerShortId || !hasCompletedMeasureResponses) {
    return null;
  }

  return (
    <ClientInformationProgress
      {...passthroughProps}
      shouldAbbreviateScoreCategoryLabels={true}
      clientUserShortId={clientUserShortId}
      providerShortId={providerShortId}
      isProvider={isProvider}
      isShownWithinPaperworkFlow={isShownWithinPaperworkFlow}
      timezone={timezone}
      onClick={measureBundles ? handleMeasureResponseClick : undefined}
      ref={mergedRefs}
    />
  );
}
